/**
 * @FUNCTION_NAME validateConditionJsonStructure
 * @DESCRIPTION 상태 천이 조건 JSON을 검증하고 유효성을 확인하는 서비스 함수. 중첩된 복합 조건 및 단순 조건의 구조를
 *              재귀적으로 검증하여 모든 조건이 규정된 형식과 허용된 컨텍스트 데이터 이름을 사용하는지 검증한다.
 *
 * @INPUT_PARAMETERS
 *    - {String|Object} conditionJson: 검증할 조건 JSON 문자열 또는 객체. 복합 조건과 단순 조건으로 구성된 계층 구조
 *    - {Array<String>} allowedDataNames: 허용된 dataName 목록. 설비 유형별 상태 천이 조건에 사용 가능한 해당 설비 유형의 상태 dataName 목록
 *
 * @OUTPUT_DESCRIPTION
 *    - 입력된 조건이 구조적으로 유효하고 해당 설비 유형의 컨텍스트 데이터 이름을 사용하는 지 검증한다.
 *    - 오류 발생 시 오류 메시지와 함께 예외를 발생시킨다.
 *
 * @RETURN
 *    - throw Error or 반환 값 없음
 *
 * @EXCEPTIONS
 *    - {Error} ParsingError: 유효하지 않은 JSON 형식인 경우 발생
 *    - {Error} InvalidParameterError: 필수 파라미터가 누락되거나 유효하지 않은 경우 발생
 *
 * @MODIFICATION_HISTORY
 *    - 2025-04-23: 문준호 - 초기 구현
 */
function validateConditionJsonStructure(conditionJson, allowedDataNames) {
    let condition;

    if (!conditionJson || !allowedDataNames) {
        throw new Error("InvalidParameterError : parameters are required");
    }

    try {
        condition = typeof conditionJson === "string" ? JSON.parse(conditionJson) : conditionJson;
    } catch (error) {
        throw new Error(`ParsingError: ${error.message}`);
    }

    validateCondition(condition, allowedDataNames);
}

/**
 * @FUNCTION_NAME validateCondition
 * @DESCRIPTION 조건 객체의 유효성을 재귀적으로 검사하는 내부 함수. 복합 조건과 단순 조건의 구조를 검증하고
 *              허용된 컨텍스트 데이터 이름을 사용하는지 확인한다.
 *
 * @INPUT_PARAMETERS
 *    - {Object} condition: 검증할 조건 객체. 복합 조건 또는 단순 조건의 구조를 가짐
 *    - {Array<String>} allowedDataNames: 허용된 컨텍스트 데이터 이름 목록. 조건에서 사용 가능한 컨텍스트 데이터 이름 목록
 *
 * @OUTPUT_DESCRIPTION
 *    - 조건 객체의 구조적 유효성과 허용된 컨텍스트 데이터 이름 사용 여부를 검증한다.
 *    - 오류 발생 시 예외를 발생시킨다.
 *
 * @EXCEPTIONS
 *    - {Error} MissingTypeError: 조건 타입을 찾을 수 없는 경우 발생
 *    - {Error} UnknownTypeError: compound 또는 simple 외의 알 수 없는 조건 타입이 지정된 경우 발생
 *
 * @MODIFICATION_HISTORY
 *    - 2025-04-23: 문준호 - 초기 구현
 */
function validateCondition(condition, allowedDataNames) {
    if (!condition.type) {
        throw new Error("MissingTypeError : type is required");
    }

    switch (condition.type) {
        case "compound":
            validateCompoundCondition(condition, allowedDataNames);
            break;
        case "simple":
            validateSimpleCondition(condition, allowedDataNames);
            break;
        default:
            throw new Error(`UnknownTypeError: ${condition.type}`);
    }
}

/**
 * @FUNCTION_NAME validateCompoundCondition
 * @DESCRIPTION 복합 조건 객체의 유효성을 검사하는 내부 함수. 논리 연산자와 하위 조건들의 구조를 검증하고
 *              허용된 컨텍스트 데이터 이름을 사용하는지 확인한다.
 *
 * @INPUT_PARAMETERS
 *    - {Object} condition: 검증할 복합 조건 객체
 *    - {Array<String>} allowedDataNames: 허용된 컨텍스트 데이터 이름 목록
 *
 * @OUTPUT_DESCRIPTION
 *    - 복합 조건 객체의 구조적 유효성을 검증한다.
 *    - 논리 연산자의 유효성과 개수를 검증한다.
 *    - 하위 조건들의 유효성을 재귀적으로 검증한다.
 *    - 오류 발생 시 예외를 발생시킨다.
 *
 * @EXCEPTIONS
 *    - {Error} MissingConditionsError: conditions가 없거나 유효하지 않은 경우 발생
 *    - {Error} InvalidConditionsError: conditions가 유효하지 않은 경우 발생
 *    - {Error} MissingLogicalOperatorsError: logicalOperators가 없거나 유효하지 않은 경우 발생
 *    - {Error} InvalidLogicalOperatorsError: 유효하지 않은 logicalOperators 경우 발생
 *    - {Error} InvalidLogicalOperatorsCountError: logicalOperators 개수가 올바르지 않은 경우 발생
 *
 * @MODIFICATION_HISTORY
 *    - 2025-04-23: 문준호 - 초기 구현
 */
function validateCompoundCondition(condition, allowedDataNames) {
    if (!condition.logicalOperators) {
        throw new Error("MissingLogicalOperatorsError : logicalOperators is required");
    }

    if (!Array.isArray(condition.logicalOperators)) {
        throw new Error("InvalidLogicalOperatorsError : logicalOperators must be an array");
    }

    if (condition.logicalOperators.length < 1) {
        throw new Error("MissingLogicalOperatorsError : logicalOperators must have at least one element");
    }

    const validOperators = ["AND", "OR"];
    for (const op of condition.logicalOperators) {
        if (!validOperators.includes(op.toUpperCase())) {
            throw new Error(`InvalidLogicalOperatorsError: ${op}`);
        }
    }

    if (!condition.conditions) {
        throw new Error("MissingConditionsError : conditions is required");
    }

    if (!Array.isArray(condition.conditions)) {
        throw new Error("InvalidConditionsError : conditions must be an array");
    }

    if (condition.conditions.length < 1) {
        throw new Error("InvalidConditionsError : conditions must have at least one element");
    }

    if (condition.logicalOperators.length !== condition.conditions.length - 1) {
        throw new Error("InvalidLogicalOperatorsCountError");
    }

    for (const subCondition of condition.conditions) {
        validateCondition(subCondition, allowedDataNames);
    }
}

/**
 * @FUNCTION_NAME validateSimpleCondition
 * @DESCRIPTION 단순 조건 객체의 유효성을 검사하는 내부 함수. 필수 필드와 비교 연산자를 검증한다.
 *
 * @INPUT_PARAMETERS
 *    - {Object} condition: 검증할 단순 조건 객체
 *    - {Array<String>} allowedDataNames: 허용된 dataName 목록
 *
 * @OUTPUT_DESCRIPTION
 *    - 단순 조건 객체의 구조적 유효성을 검증한다.
 *    - 필수 필드의 유효성을 검증한다.
 *    - 비교 연산자의 유효성을 검증한다.
 *    - 오류 발생 시 예외를 발생시킨다.
 *
 * @EXCEPTIONS
 *    - {Error} MissingDataNameError: dataName이 없는 경우 발생
 *    - {Error} MissingComparisonOperatorError: comparisonOperator가 없는 경우 발생
 *    - {Error} MissingValueError: value가 없는 경우 발생
 *    - {Error} InvalidComparisonOperatorError: 유효하지 않은 비교 연산자인 경우 발생
 *    - {Error} InvalidDataNameError: 허용되지 않은 dataName인 경우 발생
 *
 * @MODIFICATION_HISTORY
 *    - 2025-04-23: 문준호 - 초기 구현
 */

function validateSimpleCondition(condition, allowedDataNames) {
    if (!condition.dataName) {
        throw new Error("MissingDataNameError : dataName is required");
    }

    if (!condition.comparisonOperator) {
        throw new Error("MissingComparisonOperatorError : comparisonOperator is required");
    }

    if (condition.value === undefined) {
        throw new Error("MissingValueError : value is required");
    }

    if (!allowedDataNames.includes(condition.dataName)) {
        throw new Error(`InvalidDataNameError: ${condition.dataName} is not allowed`);
    }

    const validComparisonOperators = ["eq", "neq", "gt", "lt", "gte", "lte"];

    if (!validComparisonOperators.includes(condition.comparisonOperator)) {
        throw new Error(
            `InvalidComparisonOperatorError: ${condition.comparisonOperator} is not valid ComparisonOperator`
        );
    }
}

export default validateConditionJsonStructure;